// we will add other channels once we start supporting them
export var ConversationChannelType;
(function (ConversationChannelType) {
    /**
     * For cases when we cannot form a valid channel
     * Initial use case - to cover data associated with deleted users migrated from Zendesk
     */
    ConversationChannelType["Unknown"] = "unknown";
    ConversationChannelType["Chat"] = "chat";
    ConversationChannelType["Email"] = "email";
    ConversationChannelType["Phone"] = "phone";
    ConversationChannelType["TwilioSMS"] = "twilio-sms";
})(ConversationChannelType || (ConversationChannelType = {}));
export var ConversationParticipantEntity;
(function (ConversationParticipantEntity) {
    ConversationParticipantEntity["Unknown"] = "unknown";
    ConversationParticipantEntity["Client"] = "client";
    ConversationParticipantEntity["PlatformUser"] = "platform_user";
    ConversationParticipantEntity["Bot"] = "bot";
    ConversationParticipantEntity["Rsm"] = "rsm";
})(ConversationParticipantEntity || (ConversationParticipantEntity = {}));
export var ConversationBot;
(function (ConversationBot) {
    ConversationBot["PortalBot"] = "portal_bot";
    ConversationBot["FlexBot"] = "flex_bot";
})(ConversationBot || (ConversationBot = {}));
export var ConversationStatus;
(function (ConversationStatus) {
    ConversationStatus["New"] = "new";
    ConversationStatus["Open"] = "open";
    ConversationStatus["Snoozed"] = "snoozed";
    ConversationStatus["OnHold"] = "on_hold";
    ConversationStatus["Solved"] = "solved";
    ConversationStatus["Closed"] = "closed";
})(ConversationStatus || (ConversationStatus = {}));
export const CONVERSATION_STATUS_LABELS = {
    [ConversationStatus.New]: 'New',
    [ConversationStatus.Open]: 'Open',
    [ConversationStatus.Snoozed]: 'Snoozed',
    [ConversationStatus.OnHold]: 'On Hold',
    [ConversationStatus.Solved]: 'Solved',
    [ConversationStatus.Closed]: 'Closed',
};
export var ConversationSnoozePreset;
(function (ConversationSnoozePreset) {
    ConversationSnoozePreset["InThreeMinutes"] = "in_three_minutes";
    ConversationSnoozePreset["TomorrowAtNine"] = "tomorrow_at_9am";
    /** The next day the partner is open at 9AM, skipping holidays. */
    ConversationSnoozePreset["NextBusinessDayAtNine"] = "next_business_day_at_9am";
    ConversationSnoozePreset["ThreeDays"] = "three_days";
    /** The next Monday at 9AM in the conversation partner's timezone. */
    ConversationSnoozePreset["NextWeek"] = "next_week";
    ConversationSnoozePreset["TwoWeeks"] = "two_weeks";
    ConversationSnoozePreset["OneMonth"] = "one_month";
})(ConversationSnoozePreset || (ConversationSnoozePreset = {}));
export const CONVERSATION_SNOOZE_PRESET_LABELS = {
    [ConversationSnoozePreset.InThreeMinutes]: 'In 3 minutes',
    [ConversationSnoozePreset.TomorrowAtNine]: 'Tomorrow at 9AM',
    [ConversationSnoozePreset.NextBusinessDayAtNine]: 'Next business day at 9AM',
    [ConversationSnoozePreset.ThreeDays]: 'In three days',
    [ConversationSnoozePreset.NextWeek]: 'Next Monday at 9AM',
    [ConversationSnoozePreset.TwoWeeks]: 'In two weeks',
    [ConversationSnoozePreset.OneMonth]: 'A month from now',
};
export function isConversationSnoozePreset(value) {
    return Object.values(ConversationSnoozePreset).includes(value);
}
export var ConversationChatApp;
(function (ConversationChatApp) {
    ConversationChatApp["ClientGateway"] = "client_gateway";
    ConversationChatApp["Portal"] = "portal";
})(ConversationChatApp || (ConversationChatApp = {}));
export var ConversationParticipationMode;
(function (ConversationParticipationMode) {
    /** default mode */
    ConversationParticipationMode["Engage"] = "engage";
    /** Internal mode: agents will have this mode   */
    ConversationParticipationMode["Assist"] = "assist";
})(ConversationParticipationMode || (ConversationParticipationMode = {}));
export var ConversationMessageDispatchStatus;
(function (ConversationMessageDispatchStatus) {
    ConversationMessageDispatchStatus["Dispatching"] = "dispatching";
    ConversationMessageDispatchStatus["Dispatched"] = "dispatched";
    ConversationMessageDispatchStatus["Failed"] = "failed";
    ConversationMessageDispatchStatus["Seen"] = "seen";
})(ConversationMessageDispatchStatus || (ConversationMessageDispatchStatus = {}));
export var ConversationEmailDirection;
(function (ConversationEmailDirection) {
    ConversationEmailDirection["Incoming"] = "incoming";
    ConversationEmailDirection["Outgoing"] = "outgoing";
})(ConversationEmailDirection || (ConversationEmailDirection = {}));
export var ConversationSmsDirection;
(function (ConversationSmsDirection) {
    ConversationSmsDirection["Incoming"] = "incoming";
    ConversationSmsDirection["Outgoing"] = "outgoing";
})(ConversationSmsDirection || (ConversationSmsDirection = {}));
/**
 * Twilio Lookup v2 line types that may receive session SMS. `nonfixedvoip` (Google Voice,
 * TextNow, …) is included because those numbers receive SMS in practice — live QA on dev-ada
 * had delivered receipts to one before the mobile-only gate started rejecting it. Fixed `voip`,
 * `landline`, `tollFree` etc. stay out. Shared by backend and portal so the set never diverges.
 */
export const SENDABLE_SMS_LINE_TYPES = new Set(['mobile', 'nonfixedvoip']);
/**
 * SMS session timer policy, shared so the backend fallbacks, the write guard and the Preferences
 * page never disagree. Defaults apply when a session row has no snapshotted policy (backend) or
 * the platform rows are absent (portal); the DML seeds the same values. Zero nudges is a valid
 * "straight to goodbye" policy, so its minimum is 0.
 */
export const SMS_SESSION_DEFAULT_IDLE_SECONDS = 90;
export const SMS_SESSION_DEFAULT_NUDGE_ATTEMPTS = 3;
export const SMS_SESSION_IDLE_SECONDS_MIN = 1;
export const SMS_SESSION_NUDGE_ATTEMPTS_MIN = 0;
export var HealthStatus;
(function (HealthStatus) {
    HealthStatus["healthy"] = "healthy";
    HealthStatus["not_reachable"] = "not_reachable";
})(HealthStatus || (HealthStatus = {}));
export var SendUnlessConditionType;
(function (SendUnlessConditionType) {
    SendUnlessConditionType["AgentReply"] = "agent_reply";
    SendUnlessConditionType["ClientReply"] = "client_reply";
    SendUnlessConditionType["StatusChange"] = "status_change";
    SendUnlessConditionType["AssigneeChange"] = "assignee_change";
})(SendUnlessConditionType || (SendUnlessConditionType = {}));
export var ConversationDraftSendOptionSection;
(function (ConversationDraftSendOptionSection) {
    ConversationDraftSendOptionSection["SendAnd"] = "send-and";
    ConversationDraftSendOptionSection["SendLater"] = "send-later";
})(ConversationDraftSendOptionSection || (ConversationDraftSendOptionSection = {}));
export var ConversationDraftSideEffectActionType;
(function (ConversationDraftSideEffectActionType) {
    ConversationDraftSideEffectActionType["Status"] = "status";
    ConversationDraftSideEffectActionType["Snooze"] = "snooze";
})(ConversationDraftSideEffectActionType || (ConversationDraftSideEffectActionType = {}));
export var ConversationDraftScope;
(function (ConversationDraftScope) {
    ConversationDraftScope["Unscheduled"] = "unscheduled";
    ConversationDraftScope["Scheduled"] = "scheduled";
})(ConversationDraftScope || (ConversationDraftScope = {}));
